import React, { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { Button, Spin } from 'antd';
import { toast } from 'react-toastify';
import ContentLayout from '../../../layout/ContentLayout';
import { Container } from '../../../components/content';
import { FloatingLabelInput, FloatingLabelSearchSelect } from '../../../components/elements/floatingLabelInput/FloatingLabelInput';
import {
  fetchProductById,
  fetchProducts,
  getProductSubscriptionPackage,
  upsertProductSubscriptionPackage,
  deleteProductSubscriptionPackage,
} from '../../../api/webshop-products-orders-api';
import styles from '../../../styles/mobileInfoPage.module.scss';

const emptyPricing = { threeMonth: '', sixMonth: '', twelveMonth: '' };

export default function IzmeniPaket() {
  const { id } = useParams();
  const navigate = useNavigate();

  const [product, setProduct] = useState(null);
  const [hasExistingConfig, setHasExistingConfig] = useState(false);
  const [componentProductIds, setComponentProductIds] = useState([]);
  const [selectedProductsMap, setSelectedProductsMap] = useState({});
  // Units of each product in every monthly shipment of one package, keyed by product id.
  const [quantities, setQuantities] = useState({});
  const [pricing, setPricing] = useState(emptyPricing);

  const [searchTerm, setSearchTerm] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searchLoading, setSearchLoading] = useState(false);

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const loadData = useCallback(async () => {
    setLoading(true);
    const [productResult, packageResult] = await Promise.all([
      fetchProductById(id),
      getProductSubscriptionPackage(id),
    ]);

    if (!productResult.success || !productResult.data) {
      toast.error(productResult.message || 'Greška pri učitavanju proizvoda.');
      navigate('/proizvodi?tab=pretplatnicki-paketi');
      return;
    }
    setProduct(productResult.data);

    const pkg = packageResult.success ? packageResult.data : null;
    if (pkg) {
      setHasExistingConfig(true);
      setComponentProductIds(pkg.componentProducts.map((p) => p._id));
      setSelectedProductsMap(
        Object.fromEntries(pkg.componentProducts.map((p) => [p._id, { name: p.name }]))
      );
      setQuantities(
        Object.fromEntries(pkg.componentProducts.map((p) => [p._id, p.quantity ?? 1]))
      );
      setPricing({
        threeMonth: pkg.pricing.threeMonth,
        sixMonth: pkg.pricing.sixMonth,
        twelveMonth: pkg.pricing.twelveMonth,
      });
    } else {
      setHasExistingConfig(false);
      setComponentProductIds([]);
      setSelectedProductsMap({});
      setQuantities({});
      setPricing(emptyPricing);
    }
    setLoading(false);
  }, [id, navigate]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const loadSearchResults = useCallback(async (search) => {
    setSearchLoading(true);
    const { data } = await fetchProducts({ page: 1, limit: 20, search: search || null });
    if (data) {
      const filtered = data.products.filter(
        (p) => p._id !== id && p.isSubscriptionOnlyProduct !== true
      );
      setSearchResults(filtered);
    }
    setSearchLoading(false);
  }, [id]);

  useEffect(() => {
    const timer = setTimeout(() => loadSearchResults(searchTerm), 300);
    return () => clearTimeout(timer);
  }, [searchTerm, loadSearchResults]);

  const handleComponentProductsChange = (ids) => {
    const newMap = { ...selectedProductsMap };
    ids.forEach((productId) => {
      if (!newMap[productId]) {
        const found = searchResults.find((p) => p._id === productId);
        if (found) newMap[productId] = { name: found.name };
      }
    });
    setSelectedProductsMap(newMap);
    setQuantities((prev) =>
      Object.fromEntries(
        ids.map((productId) => [productId, prev[productId] ?? 1])
      )
    );
    setComponentProductIds(ids);
  };

  const handleQuantityChange = (productId, value) => {
    setQuantities((prev) => ({ ...prev, [productId]: value }));
  };

  const componentProductOptions = (() => {
    const byId = new Map(searchResults.map((p) => [p._id, p.name]));
    componentProductIds.forEach((productId) => {
      if (!byId.has(productId)) {
        byId.set(productId, selectedProductsMap[productId]?.name || productId);
      }
    });
    return Array.from(byId.entries()).map(([value, label]) => ({ value, label }));
  })();

  const handlePricingChange = (field, value) => {
    setPricing((prev) => ({ ...prev, [field]: value }));
  };

  const validate = () => {
    if (componentProductIds.length === 0) {
      toast.error('Dodajte bar jedan proizvod u paket.');
      return false;
    }
    const invalidQuantity = componentProductIds.some((productId) => {
      const num = Number(quantities[productId]);
      return !Number.isInteger(num) || num < 1 || num > 20;
    });
    if (invalidQuantity) {
      toast.error(
        'Količina svakog proizvoda u paketu mora biti ceo broj od 1 do 20.'
      );
      return false;
    }
    for (const key of ['threeMonth', 'sixMonth', 'twelveMonth']) {
      const num = Number(pricing[key]);
      if (pricing[key] === '' || isNaN(num) || num < 0) {
        toast.error('Unesite validne cene za sva tri perioda pretplate.');
        return false;
      }
    }
    return true;
  };

  const handleSave = async () => {
    if (!validate()) return;
    setSaving(true);
    const { success, message } = await upsertProductSubscriptionPackage(id, {
      componentProducts: componentProductIds.map((productId) => ({
        productId,
        quantity: Number(quantities[productId]),
      })),
      pricing: {
        threeMonth: Number(pricing.threeMonth),
        sixMonth: Number(pricing.sixMonth),
        twelveMonth: Number(pricing.twelveMonth),
      },
    });
    if (success) {
      toast.success('Paket za pretplatu je uspešno sačuvan.');
      navigate('/proizvodi?tab=pretplatnicki-paketi');
    } else {
      toast.error(message || 'Greška pri čuvanju paketa za pretplatu.');
    }
    setSaving(false);
  };

  const handleDelete = async () => {
    setDeleting(true);
    const { success, message } = await deleteProductSubscriptionPackage(id);
    if (success) {
      toast.success('Konfiguracija paketa je uklonjena.');
      navigate('/proizvodi?tab=pretplatnicki-paketi');
    } else {
      toast.error(message || 'Greška pri uklanjanju konfiguracije paketa.');
    }
    setDeleting(false);
  };

  if (loading || !product) {
    return (
      <Container>
        <ContentLayout>
          <div className={styles.loadingContainer}>
            <Spin size="large" />
          </div>
        </ContentLayout>
      </Container>
    );
  }

  return (
    <Container>
      <Link to="/proizvodi?tab=pretplatnicki-paketi" className="link_btn" style={{ marginBottom: '12px' }}>
        <img alt="back" src="/assets/icons/chevron.svg" />
        <span>Nazad</span>
      </Link>

      <ContentLayout pt={true}>
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: '32px',
            flexWrap: 'wrap',
            gap: '16px',
          }}
        >
          <h2 className={styles.pageTitle} style={{ margin: 0 }}>
            Podešavanje paketa: {product.name}
          </h2>
          {hasExistingConfig && (
            <Button className="btn-primary red-button" onClick={handleDelete} loading={deleting}>
              Obriši konfiguraciju
            </Button>
          )}
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: '32px' }}>
          <section>
            <h3 className={styles.sectionTitle}>Proizvodi u paketu</h3>
            <FloatingLabelSearchSelect
              label="Pretraži i dodaj proizvode"
              mode="multiple"
              value={componentProductIds}
              onChange={handleComponentProductsChange}
              onSearch={setSearchTerm}
              loading={searchLoading}
              options={componentProductOptions}
            />
            {componentProductIds.length > 0 && (
              <div
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '12px',
                  marginTop: '16px',
                }}
              >
                {componentProductIds.map((productId) => (
                  <div
                    key={productId}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      gap: '16px',
                    }}
                  >
                    <span style={{ flex: 1, minWidth: 0 }}>
                      {selectedProductsMap[productId]?.name || productId}
                    </span>
                    <div style={{ width: '160px', flexShrink: 0 }}>
                      <FloatingLabelInput
                        label="Količina po isporuci"
                        type="number"
                        min={1}
                        max={20}
                        value={quantities[productId] ?? 1}
                        onChange={(e) => handleQuantityChange(productId, e.target.value)}
                      />
                    </div>
                  </div>
                ))}
              </div>
            )}
          </section>

          <section>
            <h3 className={styles.sectionTitle}>Cene po periodu pretplate</h3>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '32px' }}>
              <FloatingLabelInput
                label="3 meseca"
                type="number"
                value={pricing.threeMonth}
                onChange={(e) => handlePricingChange('threeMonth', e.target.value)}
              />
              <FloatingLabelInput
                label="6 meseci"
                type="number"
                value={pricing.sixMonth}
                onChange={(e) => handlePricingChange('sixMonth', e.target.value)}
              />
              <FloatingLabelInput
                label="12 meseci"
                type="number"
                value={pricing.twelveMonth}
                onChange={(e) => handlePricingChange('twelveMonth', e.target.value)}
              />
            </div>
          </section>

          <div style={{ display: 'flex', gap: '16px' }}>
            <Button className="btn-primary" type="primary" onClick={handleSave} loading={saving}>
              Sačuvaj
            </Button>
          </div>
        </div>
      </ContentLayout>
    </Container>
  );
}
